import React from 'react';
import { Box, Typography, Link as MuiLink } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

interface TocItem {
  level: number;
  text: string;
  id: string;
}

interface TableOfContentsProps {
  content: string;
}

const TableOfContents: React.FC<TableOfContentsProps> = ({ content }) => {
  const theme = useTheme();

  const extractHeadings = (markdownContent: string): TocItem[] => {
    const headingRegex = /^(#{1,6})\s+(.+)$/gm;
    const headings: TocItem[] = [];
    let match;

    while ((match = headingRegex.exec(markdownContent)) !== null) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = text
        .toLowerCase()
        .replace(/[^а-яёa-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');

      headings.push({ level, text, id });
    }

    return headings;
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      // Добавляем хэш в URL без перезагрузки страницы
      window.history.pushState(null, '', `#${id}`);
    }
  };

  const toc = extractHeadings(content);

  if (toc.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        p: 2,
        mb: 3,
        borderRadius: 2,
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Содержание
      </Typography>
      <Box>
        {toc.map((item, index) => (
          <MuiLink
            key={index}
            href={`#${item.id}`}
            onClick={(e) => handleClick(e, item.id)}
            underline="hover"
            sx={{
              display: 'block',
              color: 'text.primary',
              textDecoration: 'none',
              mb: 1,
              pl: (item.level - 1) * 2,
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            {item.text}
          </MuiLink>
        ))}
      </Box>
    </Box>
  );
};

export default TableOfContents;